import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMonitorData } from '../../../services/monitor/data/MonitorDataService';
import { GET_MONITOR_DATA } from '../../../query/monitor/data/keys';
import { MonitorDataResponse } from '../../../../models/monitor/MonitorDataResponse';
import { PeriodType } from '../../../../types/prompt/PeriodType';
import { MonitorType } from '../../../../types/monitor/MonitorType';

export interface MonitorDataParams {
  monitorId: number;
  keyword: string;
  monitorPeriod: PeriodType;
  chatType: MonitorType;
  from: Date | null;
  to: Date | null;
  page?: number;
  pageSize?: number;
  forDate?: string;
  models?: string[];
}

export const useGetMonitorData = (params: MonitorDataParams | null) => {
  const queryClient = useQueryClient();

  const fromTimestamp = params?.from?.getTime() ?? null;
  const toTimestamp = params?.to?.getTime() ?? null;

  const query = useQuery<MonitorDataResponse>({
    queryKey: [
      GET_MONITOR_DATA,
      params?.monitorId,
      params?.chatType,
      params?.monitorPeriod,
      fromTimestamp,
      toTimestamp,
      params?.page,
      params?.pageSize,
      params?.keyword,
      params?.forDate,
      params?.models,
    ],
    queryFn: () => {
      if (!params || !params.from || !params.to) {
        throw new Error('Params with dates are required');
      }
      return getMonitorData(
        params.monitorId,
        params.keyword,
        params.monitorPeriod,
        params.chatType,
        params.from,
        params.to,
        params.page,
        params.pageSize,
        undefined,
        params.forDate,
        params.models
      );
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled:
      !!params &&
      !!params.from &&
      !!params.to &&
      !!params.monitorPeriod &&
      !!params.chatType &&
      !!params.monitorId &&
      !!params.keyword,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_MONITOR_DATA, params?.monitorId],
      refetchType: 'active',
    });
  };

  return {
    monitorData: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    invalidate,
  };
};
